import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import clsx from "clsx";
import Button from "../Button/Button";
import Tab from "../Tab/Tab";
import Badge from "../Badge/Badge";

// Ícone de sino estilo Fluent UI (SVG)
const BellIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width={23}
    height={23}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.85}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      d="M18 16v-5a6 6 0 10-12 0v5a2 2 0 01-2 2h16a2 2 0 01-2-2zm-6 6a2 2 0 002-2H10a2 2 0 002 2z"
      fill="none"
    />
  </svg>
);

type NotificationType = {
  id: string | number;
  title: string;
  message: string;
  date: string;
  read?: boolean;
  favorite?: boolean;
  link?: string;
  [key: string]: unknown;
};

type NotificationTab = "unread" | "read" | "favorite";

type NotificationProps = {
  notifications: NotificationType[];
  onMarkAsRead?: (id: NotificationType["id"]) => void;
  onMarkAsUnread?: (id: NotificationType["id"]) => void;
  onMarkAsFavorite?: (id: NotificationType["id"]) => void;
  onRemoveFavorite?: (id: NotificationType["id"]) => void;
  onOpen?: (notification: NotificationType) => void;
  theme?: "light" | "dark";
  className?: string;
  style?: React.CSSProperties;
  bellButtonAriaLabel?: string;
};

const tabLabels: Record<NotificationTab, string> = {
  unread: "Não lidas",
  read: "Lidas",
  favorite: "Favoritas",
};

function getTabNotifications(
  notifications: NotificationType[],
  tab: NotificationTab
): NotificationType[] {
  switch (tab) {
    case "unread":
      return notifications.filter((n) => !n.read);
    case "read":
      return notifications.filter((n) => n.read && !n.favorite);
    case "favorite":
      return notifications.filter((n) => n.favorite);
    default:
      return [];
  }
}


const NotificationItem: React.FC<{
  notification: NotificationType;
  onMarkAsRead?: () => void;
  onMarkAsUnread?: () => void;
  onMarkAsFavorite?: () => void;
  onRemoveFavorite?: () => void;
  onOpen?: () => void;
  theme?: "light" | "dark";
}> = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAsFavorite,
  onRemoveFavorite,
  onOpen,
  theme = "light",
}) => (
  <li
    className={clsx(
      "flex items-start gap-3 p-3 rounded-md mb-1 group transition",
      notification.read
        ? theme === "dark"
          ? "bg-[#212121] hover:bg-[#23272e]"
          : "bg-gray-100 hover:bg-gray-200"
        : theme === "dark"
        ? "bg-[#1e273a] hover:bg-[#23272e]"
        : "bg-white hover:bg-blue-50",
      "shadow-sm"
    )}
    tabIndex={0}
    role="listitem"
    aria-label={notification.title}
    style={{ fontFamily: "Grotesk, sans-serif" }} // <--- Grotesk para o item todo
  >
    <div
      className="flex-1 min-w-0 cursor-pointer"
      onClick={onOpen}
      tabIndex={-1}
    >
      <div
        className="font-semibold truncate text-base"
        style={{ fontFamily: "Inter, sans-serif" }} // <--- Inter para o título
      >
        {notification.title}
      </div>
      <div className="text-sm text-gray-500 truncate">{notification.message}</div>
      <div className="text-xs text-gray-400 mt-1">{notification.date}</div>
    </div>
    <div className="flex flex-col gap-1 items-end ml-2">
      {!notification.read && (
        <Button
          theme={theme}
          size="tiny"
          variant="icon"
          ariaLabel="Marcar como lida"
          buttonClassName="bg-transparent hover:bg-blue-100 text-blue-600"
          icon={
            <svg width="19" height="19" fill="none" stroke="currentColor" strokeWidth={2.1} viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          }
          onClick={() => { onMarkAsRead?.(); }}
        />
      )}
      {notification.read && (
        <Button
          theme={theme}
          size="tiny"
          variant="icon"
          ariaLabel="Marcar como não lida"
          buttonClassName="bg-transparent hover:bg-yellow-50 text-yellow-600"
          icon={
            <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          }
          onClick={() => { onMarkAsUnread?.(); }}
        />
      )}
      {notification.favorite ? (
        <Button
          theme={theme}
          size="tiny"
          variant="icon"
          ariaLabel="Remover dos favoritos"
          buttonClassName="bg-transparent hover:bg-yellow-100 text-yellow-500"
          icon={
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63l-7.19.61 5.46 4.73L5.82 21z" />
            </svg>
          }
          onClick={() => { onRemoveFavorite?.(); }}
        />
      ) : (
        <Button
          theme={theme}
          size="tiny"
          variant="icon"
          ariaLabel="Adicionar aos favoritos"
          buttonClassName="bg-transparent hover:bg-yellow-50 text-yellow-500"
          icon={
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63l-7.19.61 5.46 4.73L5.82 21z" />
            </svg>
          }
          onClick={() => { onMarkAsFavorite?.(); }}
        />
      )}
      {notification.link && (
        <Button
          theme={theme}
          size="tiny"
          variant="icon"
          ariaLabel="Abrir notificação"
          buttonClassName="bg-transparent hover:bg-green-50 text-green-600"
          icon={
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" />
            </svg>
          }
          onClick={onOpen ?? (() => {})}
        />
      )}
    </div>
  </li>
);

export const Notification: React.FC<NotificationProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAsFavorite,
  onRemoveFavorite,
  onOpen,
  theme = "light",
  className,
  style,
  bellButtonAriaLabel = "Notificações",
}) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>("unread");
  const balloonRef = useRef<HTMLDivElement>(null);
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  // Memoize handlers
  const handleMarkAsRead = useCallback(
    (id: NotificationType["id"]) => {
      onMarkAsRead?.(id);
    },
    [onMarkAsRead]
  );

  const handleMarkAsUnread = useCallback(
    (id: NotificationType["id"]) => {
      onMarkAsUnread?.(id);
    },
    [onMarkAsUnread]
  );

  const handleMarkAsFavorite = useCallback(
    (id: NotificationType["id"]) => {
      onMarkAsFavorite?.(id);
    },
    [onMarkAsFavorite]
  );

  const handleRemoveFavorite = useCallback(
    (id: NotificationType["id"]) => {
      onRemoveFavorite?.(id);
    },
    [onRemoveFavorite]
  );

  const handleOpen = useCallback(
    (notification: NotificationType) => {
      onOpen?.(notification);
    },
    [onOpen]
  );

  // Fecha balão ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        balloonRef.current &&
        !balloonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler, true);
    };
  }, [open]);

  const currentNotifications = getTabNotifications(notifications, tab);

  return (
    <div
      className={clsx("relative", className)}
      style={{ ...style, fontFamily: "Grotesk, sans-serif" }} // <--- Grotesk para o balão todo
    >
      <Button
        theme={theme}
        variant="icon"
        size="tiny"
        ariaLabel={bellButtonAriaLabel}
        buttonClassName={clsx(
          "relative w-11 h-11 flex items-center justify-center rounded-full shadow transition",
          theme === "dark"
            ? "bg-[#27292e] hover:bg-[#23272e] text-white"
            : "bg-white hover:bg-blue-50 text-blue-700"
        )}
        icon={<BellIcon />}
        onClick={() => setOpen((v) => !v)}
      />
      {unreadCount > 0 && (
        <Badge
          color="danger"
          theme={theme}
          size="sm"
          rounded="full"
          className="absolute top-1.5 right-1.5 z-10 font-bold"
          style={{ minWidth: 20, height: 20, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          ariaLabel={`${unreadCount} notificações não lidas`}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </Badge>
      )}
      {open && (
        <div
          ref={balloonRef}
          className={clsx(
            "absolute right-0 mt-2 rounded-2xl shadow-xl z-50",
            theme === "dark"
              ? "bg-[#282a36] text-white border border-[#23272e]"
              : "bg-white text-gray-900 border border-gray-200"
          )}
          style={{
            minWidth: 370,
            width: "auto",
            maxWidth: "90vw",
            fontFamily: "Grotesk, sans-serif", // <--- Grotesk para o balão
          }}
        >
          <Tab
            theme={theme}
            className="pt-2 px-2"
            defaultIndex={0}
            onChange={(idx: number) =>
              setTab(["unread", "read", "favorite"][idx] as NotificationTab)
            }
            activeIndex={["unread", "read", "favorite"].indexOf(tab)}
          >
            <Tab.List
              className={clsx(
                "flex flex-wrap gap-2 mb-2",
                "sm:gap-2 sm:text-base",
                "gap-1 text-sm px-1"
              )}
              style={{ fontFamily: "Inter, sans-serif" }} // <--- Inter para abas
            >
              <Tab.Item index={0}>
                {tabLabels.unread}{" "}
                {unreadCount > 0 && (
                  <span className="ml-1 text-xs bg-red-500 text-white px-2 py-0 rounded-full font-bold">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Tab.Item>
              <Tab.Item index={1}>{tabLabels.read}</Tab.Item>
              <Tab.Item index={2}>{tabLabels.favorite}</Tab.Item>
            </Tab.List>
            <Tab.Panel index={0}>
              <ul className="max-h-[370px] overflow-y-auto">
                {tab === "unread" && currentNotifications.length === 0 ? (
                  <li className="text-center text-gray-400 py-6">
                    Nenhuma notificação não lida.
                  </li>
                ) : (
                  tab === "unread" &&
                  currentNotifications.map((n) => (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      onMarkAsRead={() => handleMarkAsRead(n.id)}
                      onMarkAsFavorite={() => handleMarkAsFavorite(n.id)}
                      onOpen={() => handleOpen(n)}
                      theme={theme}
                    />
                  ))
                )}
              </ul>
            </Tab.Panel>
            <Tab.Panel index={1}>
              <ul className="max-h-[370px] overflow-y-auto">
                {tab === "read" && currentNotifications.length === 0 ? (
                  <li className="text-center text-gray-400 py-6">
                    Nenhuma notificação lida.
                  </li>
                ) : (
                  tab === "read" &&
                  currentNotifications.map((n) => (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      onMarkAsUnread={() => handleMarkAsUnread(n.id)}
                      onMarkAsFavorite={() => handleMarkAsFavorite(n.id)}
                      onOpen={() => handleOpen(n)}
                      theme={theme}
                    />
                  ))
                )}
              </ul>
            </Tab.Panel>
            <Tab.Panel index={2}>
              <ul className="max-h-[370px] overflow-y-auto">
                {tab === "favorite" && currentNotifications.length === 0 ? (
                  <li className="text-center text-gray-400 py-6">
                    Nenhuma notificação favorita.
                  </li>
                ) : (
                  tab === "favorite" &&
                  currentNotifications.map((n) => (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      onRemoveFavorite={() => handleRemoveFavorite(n.id)}
                      onOpen={() => handleOpen(n)}
                      theme={theme}
                    />
                  ))
                )}
              </ul>
            </Tab.Panel>
          </Tab>
        </div>
      )}
    </div>
  );
};

export type { NotificationType, NotificationTab, NotificationProps };

export default Notification;